import { createContext, useReducer, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { getCookies } from 'cookies-next';
import wishlistReducer from './Reducers';
import store from 'store';
import { getWishlist, addToWishlist } from '@/services/wishlist';
import { transformWishlistResponse } from '@/utils/wishlist/transformers';

const WishlistContext = createContext();

const getAuthCookies = () => {
  const { handshake, mage } = getCookies();
  return handshake && mage ? { handshake, mage } : null;
};

const getStoredItems = () => {
  const stored = store.get('wishlist');
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
  return stored.items === undefined ? stored : (stored.items ?? {});
};

const getInitialState = () => ({
  items: getStoredItems(),
  loading: false,
  error: null,
  itemsCount: null,
});

const WishlistProvider = ({ children }) => {
  const [state, dispatch] = useReducer(wishlistReducer, null, getInitialState);
  const { data: session, status } = useSession();

  useEffect(() => {
    store.set('wishlist', state.items);
  }, [state.items]);

  const fetchWishlistFromAPI = async () => {
    const auth = getAuthCookies();
    if (!auth) return null;

    dispatch({ type: 'set_loading', payload: true });
    dispatch({ type: 'set_error', payload: null });

    try {
      const res = await getWishlist(auth.handshake, auth.mage);
      if (res?.error) {
        dispatch({ type: 'set_error', payload: res.error });
        return null;
      }
      const { items, itemsCount } = transformWishlistResponse(res?.data ?? res);
      dispatch({ type: 'sync_from_api', payload: { items, itemsCount } });
      return { items, itemsCount };
    } catch (err) {
      dispatch({ type: 'set_error', payload: err?.message || 'Failed to load wishlist' });
      return null;
    } finally {
      dispatch({ type: 'set_loading', payload: false });
    }
  };

  useEffect(() => {
    if (status === 'loading') return;

    if (session?.user) {
      const run = async () => {
        const auth = getAuthCookies();
        if (!auth) return;

        const localItems = getStoredItems();
        const result = await fetchWishlistFromAPI();
        if (!result) return;

        const toMigrate = Object.keys(localItems).filter(
          (sku) => localItems[sku]?.id && !(sku in result.items)
        );
        if (toMigrate.length > 0) {
          await Promise.allSettled(
            toMigrate.map((sku) => addToWishlist(auth.handshake, auth.mage, localItems[sku].id))
          );
          await fetchWishlistFromAPI();
        }
      };
      run();
    } else {
      const stored = getStoredItems();
      if (Object.keys(stored).length > 0) {
        dispatch({ type: 'get_wl', payload: stored });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user, status]);

  return (
    <WishlistContext.Provider
      value={{
        wishlist: state.items,
        dispatch,
        loading: state.loading,
        error: state.error,
        itemsCount: state.itemsCount,
        refreshWishlist: fetchWishlistFromAPI,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export { WishlistContext, WishlistProvider };
